import React from 'react';
import { useContacts } from '../../hooks/useContacts';
import { Mail, Building, X } from 'lucide-react';
import { useUserSettings } from '../../hooks/useUserSettings';
import { useAnonymization } from '../../utils/anonymize';
import { Skeleton } from '../ui/Skeleton';

interface EngagementContactProps {
  contactId: string;
  onRemove?: () => void;
}

export default function EngagementContact({ contactId, onRemove }: EngagementContactProps) {
  const { contacts, loading, error } = useContacts();
  const { settings } = useUserSettings();
  const shouldAnonymize = settings?.anonymizing_mode || false;
  
  const contact = contacts.find(c => c.id === contactId);

  if (loading) {
    return (
      <div className="p-3">
        <Skeleton className="h-6 w-48 mb-2" />
        <Skeleton className="h-4 w-32" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-3 text-gray-400">
        Error loading contact
      </div>
    );
  }

  if (!contact) {
    return (
      <div className="p-3 text-gray-400">
        Contact not found (ID: {contactId})
      </div>
    );
  }

  return (
    <div className="flex items-center justify-between group p-3 hover:bg-dark-700 rounded-lg">
      <div className="min-w-0 flex-1">
        <div className="flex items-center space-x-3">
          <p className="text-white font-medium truncate">
            {useAnonymization(contact.first_name, 'name', shouldAnonymize)}{' '}
            {useAnonymization(contact.last_name, 'name', shouldAnonymize)}
          </p>
          {contact.company && (
            <div className="flex items-center text-sm text-gray-400">
              <Building className="w-4 h-4 mr-1" />
              {useAnonymization(contact.company.name, 'company', shouldAnonymize)}
            </div>
          )}
        </div>
        {(contact.work_email || contact.personal_email) && (
          <div className="flex items-center text-sm text-gray-400 mt-1">
            <Mail className="w-4 h-4 mr-1" />
            {useAnonymization(contact.work_email || contact.personal_email, 'email', shouldAnonymize)}
          </div>
        )}
        {contact.job_title && (
          <p className="text-sm text-gray-400 mt-1">
            {contact.job_title}
          </p>
        )}
      </div>
      {onRemove && (
        <button
          onClick={onRemove}
          className="text-gray-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
        >
          <X className="w-5 h-5" />
        </button>
      )}
    </div>
  );
}
